import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebinarRoomUrl, useWebinar } from '@immopersian/common';
import { WebinarRoom } from '../components/WebinarRoom';
import { RecordingPlayer } from '../components/RecordingPlayer';
import { LoadingScreen } from '../components/LoadingScreen';

export default function WebinarRoomPage() {
  const { webinarId } = useParams<{ webinarId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fetch webinar details to check for recording
  const { data: webinar, isLoading: isLoadingWebinar } = useWebinar(webinarId || '');

  // Fetch room URL (this will only fetch when refetch is called)
  const { data: roomUrl, isLoading, error, refetch } = useWebinarRoomUrl(webinarId);

  // Check if webinar is in replay mode
  const now = new Date();
  const endDate = webinar?.end_date ? new Date(webinar.end_date) : null;
  const validUntil = webinar?.valid_until ? new Date(webinar.valid_until) : null;
  const hasEnded = endDate ? now > endDate : false;
  const hasExpired = validUntil ? now > validUntil : false;
  const isReplayMode = hasEnded && !hasExpired;

  useEffect(() => {
    if (webinarId) {
      // Fetch the room URL when component mounts
      refetch();
    }
  }, [webinarId, refetch]);

  if (isLoading || isLoadingWebinar) {
    return (
      <div className="fixed inset-0 bg-brand-dark">
        <LoadingScreen />
      </div>
    );
  }

  // For replay mode, show recording player if recording URL exists
  if (isReplayMode && webinar?.recording_url) {
    return (
      <RecordingPlayer
        recordingUrl={webinar.recording_url}
        webinarTitle={webinar.title}
        onClose={() => navigate('/courses')}
      />
    );
  }

  // For live mode or replay without recording, fetch room URL
  if (error || !roomUrl) {
    return (
      <div className="fixed inset-0 bg-brand-dark flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
          <h2 className="text-2xl font-bold text-brand-dark mb-4">
            {t('webinar.accessDenied', 'Access Denied')}
          </h2>
          <p className="text-brand-dark/70 mb-6">
            {error
              ? t('webinar.errorLoadingRoom', 'Failed to load webinar room')
              : t('webinar.noAccess', 'You do not have access to this webinar')}
          </p>
          <button
            onClick={() => navigate('/courses')}
            className="bg-brand-terracotta hover:bg-brand-terracotta/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('webinar.backToCourses', 'Back to Courses')}
          </button>
        </div>
      </div>
    );
  }

  return <WebinarRoom roomUrl={roomUrl} />;
}
